// InboxChannel.broadcast_conversation_update goes to the whole account, so the
// payload can't carry the per-user `unread` flag — `format_conversation` omits
// it. Derive it locally when merging the update over the conversation we
// already have in state:
//   - currently-viewed conversation: read
//   - new visitor message landed: unread
//   - anything else (status change, etc.): preserve the existing value
// Client-side equivalent of Conversations::QueryService folder/filter/assignment,
// used when the local conversation list mutates after the initial server-filtered
// load (cable updates, status changes done via partial reload) and we don't
// want resolved/archived items lingering in an "unresolved" or "inbox" view.
// Keep these aligned with app/services/conversations/query_service.rb.
export interface FilterableConversation {
  status: 'open' | 'closed'
  folder?: 'inbox' | 'spam' | 'archive'
  unread?: boolean
  assignee?: { id: string; name: string } | null
}

export function filterConversationsForView<T extends FilterableConversation>(
  conversations: T[],
  filter: string,
  folder: string,
  assignment: string = 'everyone',
  currentUserId?: string
): T[] {
  return conversations.filter(c => {
    if (c.folder && c.folder !== folder) return false

    if (assignment === 'mine' && c.assignee?.id !== currentUserId) return false
    if (assignment === 'unassigned' && c.assignee) return false

    switch (filter) {
      case 'unread':
        return c.unread === true
      case 'unresolved':
      case 'longest_waiting':
        return c.status === 'open'
      case 'resolved':
        return c.status === 'closed'
      // 'all' and 'most_recent' have no status predicate
      default:
        return true
    }
  })
}

export interface MergeableConversation {
  unread?: boolean
  last_message: { sender_type: 'Visitor' | 'User'; created_at: string } | null
  last_message_at: string | null
}

export function mergeConversationUpdate<T extends MergeableConversation>(
  existing: T,
  incoming: T,
  isCurrentlyViewing = false
): T {
  const hadNewVisitorMessage =
    incoming.last_message?.sender_type === 'Visitor' &&
    incoming.last_message_at !== existing.last_message_at

  const unread = isCurrentlyViewing
    ? false
    : hadNewVisitorMessage
      ? true
      : existing.unread

  return { ...existing, ...incoming, unread }
}
